import { observer } from 'mobx-react-lite'
import OCRScopeWork from 'wunder/OCRScopeWork'
import {
  SplitViewSidebarListItem,
  SplitViewSidebarListItemTitle,
  SplitViewSidebarListItemSubtitle,
} from 'components/SplitView'
import * as React from 'react'
import { OCRScopeWorkStatusFilterTypeId } from '../OCRScopingJobSidePanelViewModel'
import OCRScopingJobMainPanelViewModel from '../../MainPanel/OCRScopingJobMainPanelViewModel'
import link from '../../../../../../infra/link'
import styles from './OCRScopeJobItem.sass'
import Checkbox from '../../../../../../components/Checkbox'
import { match } from '../../../../../../utils/pattern-match-util'
import Icon from '../../../../../../components/Icon'
import OCRScopingJobBulkActionViewModel from '../OCRScopingJobBulkActionViewModel'

interface OCRScopeJobItemProps {
  vm: OCRScopingJobBulkActionViewModel
  ocrScopingJobMainPanelVM: OCRScopingJobMainPanelViewModel
  item: OCRScopeWork
  selectedStatusFilterId: OCRScopeWorkStatusFilterTypeId
}

export default observer(function OCRScopeJobItem({
  vm,
  ocrScopingJobMainPanelVM,
  item,
  selectedStatusFilterId,
}: OCRScopeJobItemProps) {
  const jobName = item.jobName === '' ? item.fileName : item.jobName
  const showConfidenceLevel = shouldShowConfidenceLevel(
    item.status,
    item.jobStatus,
    vm.canManageOCRScopingJobs
  )
  const subtitle = showConfidenceLevel
    ? `${item.statusName} (${item.confidenceLevel}% confidence)`
    : item.statusName
  const isActive = ocrScopingJobMainPanelVM.selectedScopeWork?.id === item.id

  return (
    <SplitViewSidebarListItem
      to={link.scopingJob(item.id)}
      active={isActive}
      visual={
        <OCRScopeJobItemAction
          vm={vm}
          ocrScopingJobMainPanelVM={ocrScopingJobMainPanelVM}
          item={item}
          selectedStatusFilterId={selectedStatusFilterId}
        />
      }
    >
      <SplitViewSidebarListItemTitle>{jobName}</SplitViewSidebarListItemTitle>
      <SplitViewSidebarListItemSubtitle>
        {subtitle}
      </SplitViewSidebarListItemSubtitle>
    </SplitViewSidebarListItem>
  )
})

const OCRScopeJobItemAction = observer(function OCRScopeJobItemAction({
  vm,
  item,
  selectedStatusFilterId,
}: OCRScopeJobItemProps) {
  const canManageOCRScopingJobs = vm.canManageOCRScopingJobs
  const checked = vm.itemsSelectedForBulkAction.has(item.id)
  const toggleItemForBulkAction = () => vm.toggleItemForBulkAction(item)

  return (
    <div className={styles.itemAction}>
      {canManageOCRScopingJobs
        ? match(selectedStatusFilterId, {
            [OCRScopeWorkStatusFilterTypeId.ReadyToSubmit]: () => (
              <Checkbox
                checked={checked}
                onChange={toggleItemForBulkAction}
                muteClickEvent
              />
            ),
            [OCRScopeWorkStatusFilterTypeId.UnderReview]: () => (
              <Checkbox
                checked={checked}
                onChange={toggleItemForBulkAction}
                muteClickEvent
              />
            ),
            [OCRScopeWorkStatusFilterTypeId.ErrorOccurred]: () => (
              <Checkbox
                checked={checked}
                onChange={toggleItemForBulkAction}
                muteClickEvent
              />
            ),
            [OCRScopeWorkStatusFilterTypeId.ScopingInProgress]: () => (
              <Icon name={'loader-arrows'} />
            ),
          })
        : match(selectedStatusFilterId, {
            [OCRScopeWorkStatusFilterTypeId.ReadyToSubmit]: () => (
              <Checkbox
                checked={checked}
                onChange={toggleItemForBulkAction}
                muteClickEvent
              />
            ),
            [OCRScopeWorkStatusFilterTypeId.UnderReview]: () => (
              <Icon name={'info'} />
            ),
            [OCRScopeWorkStatusFilterTypeId.ErrorOccurred]: () => (
              <Icon name={'info'} />
            ),
            [OCRScopeWorkStatusFilterTypeId.ScopingInProgress]: () => (
              <Icon name={'loader-arrows'} />
            ),
          })}
    </div>
  )
})

/**
 * Determines whether to show the confidence level depending on the status of the job and the user's permission
 * @param status
 * @param jobStatus
 * @param canManageOCRScopingJobs
 * @returns
 */
function shouldShowConfidenceLevel(
  status: string,
  jobStatus: string,
  canManageOCRScopingJobs: boolean
): boolean {
  if (!canManageOCRScopingJobs) {
    return false
  }

  return (
    (status === 'COMPLETED' &&
      jobStatus !== 'READY_TO_SUBMIT' &&
      jobStatus !== 'SUBMITTED') ||
    status === 'REVIEW_REQUIRED' ||
    status === 'REVIEW_SUGGESTED'
  )
}
